import { useEffect, useMemo, useState } from 'react';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { checkClarificationHasUnread } from './fetchClarificationResponds';

const useClarificationUnreadByMitra = ({
  procurementId = null,
  mitraIds = [],
  clauseDetailIds = [],
  isEvaluatorView = true,
  enabled = true,
  refreshKey = 0,
} = {}) => {
  const [unreadByMitraId, setUnreadByMitraId] = useState({});

  const mitraIdsKey = useMemo(
    () =>
      [...new Set((mitraIds ?? []).filter(Boolean))]
        .map(String)
        .sort()
        .join(','),
    [mitraIds]
  );

  const clauseDetailIdsKey = useMemo(
    () =>
      [...new Set((clauseDetailIds ?? []).filter(Boolean))]
        .map(String)
        .sort()
        .join(','),
    [clauseDetailIds]
  );

  const hasAnyUnread = useMemo(
    () => Object.values(unreadByMitraId).some(Boolean),
    [unreadByMitraId]
  );

  useEffect(() => {
    if (!enabled || !procurementId || !mitraIdsKey) {
      setUnreadByMitraId({});
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) setUnreadByMitraId({});
        return;
      }

      const ids = mitraIdsKey.split(',').filter(Boolean);
      const clauseIds = clauseDetailIdsKey
        ? clauseDetailIdsKey.split(',').filter(Boolean)
        : [];
      const allowUnscoped = ids.length === 1;

      const settled = await Promise.allSettled(
        ids.map(async mitraId => {
          const hasUnread = await checkClarificationHasUnread({
            token,
            procurementId,
            procurementMitraId: mitraId,
            clauseDetailIds: clauseIds,
            isEvaluatorView,
            includeClause: true,
            includeTypeResponse: true,
            allowUnscoped,
          });
          return [mitraId, hasUnread];
        })
      );

      if (cancelled) return;

      const next = {};
      settled.forEach(result => {
        if (result.status !== 'fulfilled') return;
        const [mitraId, hasUnread] = result.value;
        next[mitraId] = hasUnread;
      });
      setUnreadByMitraId(next);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    clauseDetailIdsKey,
    enabled,
    isEvaluatorView,
    mitraIdsKey,
    procurementId,
    refreshKey,
  ]);

  return { unreadByMitraId, hasAnyUnread };
};

export default useClarificationUnreadByMitra;
